import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import type Stripe from 'stripe';
import { AppConfigService } from '../providers/config.service';
import { RecurringInterval } from '../types/product.type';
import { Promotion } from '../types/promotion.type';
import { DataLoaderService } from './data-loader.service';
import { TransferStateService } from './transfer-state.service';

@Injectable({
  providedIn: 'root',
})
export class PromotionService {
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private loader = inject(DataLoaderService);
  private transferState = inject(TransferStateService);

  public async checkCode(code: string): Promise<Promotion> {
    return await this.transferState.withStateTransfer('promo_' + code, async () => {
      return this.loader.get('promo_' + code, () => this.checkCodeInternal(code));
    });
  }

  public appliesToPrice(promo: Promotion, price: Stripe.Price) {
    const billingFrequency = price.recurring?.interval;
    if (billingFrequency === 'month' && promo.duration === 'once' && promo.amountOff) return false;
    if (!promo.appliesTo) {
      return !!price.unit_amount; // No appliesTo means it applies to every product EXCEPT FOR FREE
    }
    return promo.appliesTo.includes((price.product as Stripe.Product).id);
  }

  public appliesToProduct(
    promo: Promotion,
    product: { id: string; isFree: boolean },
    billingFrequency?: RecurringInterval
  ) {
    if (billingFrequency === 'month' && promo.duration === 'once' && promo.amountOff) return false;
    if (!promo.appliesTo) return !product.isFree; // No appliesTo means it applies to every product EXCEPT FOR FREE
    return promo.appliesTo.includes(product.id);
  }

  public appliesToAddon(promo: Promotion, addon: Stripe.Price) {
    if (promo.amountOff) return false;
    if (!promo.appliesTo) return true; // No appliesTo means it applies to every addon
    return promo.appliesTo.includes(addon.product as string);
  }

  public convertPromo(promo: Stripe.PromotionCode): Promotion {
    return {
      id: promo.id,
      code: promo.code,
      expires: promo.expires_at ? promo.expires_at * 1000 : undefined,
      amountOff: promo.coupon.amount_off ? promo.coupon.amount_off / 100 : undefined,
      appliesTo: promo.coupon.applies_to?.products,
      duration: promo.coupon.duration,
      duration_in_months: promo.coupon.duration_in_months,
      name: promo.coupon.name,
      percentOff: promo.coupon.percent_off,
    };
  }

  private async checkCodeInternal(code: string): Promise<Promotion> {
    let resp: Stripe.PromotionCode;
    try {
      resp = await lastValueFrom(
        this.http.get<Stripe.PromotionCode>(`${this.config.apiEndpoint}/billing/promotion_codes/${code}`)
      );
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (![404, 500].includes(err.status)) throw err;
    }
    if (!resp?.coupon.valid) return;

    return this.convertPromo(resp);
  }
}
